import { useAtomValue, useSetAtom } from 'jotai';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import {
  offlineNoteFailedDismissedSessionIdsAtom,
  offlineNoteFailedSessionIdsAtom,
} from '@/atoms/offline-recording';
import { useIsOfflineRecordingEnabled } from '@/features/flags';
import { isTauri } from '@/tauri/utils';
import {
  Banner,
  BannerClose,
  BannerContent,
  BannerDescription,
  BannerTitle,
} from '@heidi/design-system/banner';

const messages = defineMessages({
  dismiss: {
    defaultMessage: 'Dismiss note warning',
    id: 'xHi5H748tq',
  },
});

export const NoteFailedWarning = () => {
  const intl = useIntl();
  const isOfflineRecordingEnabled = useIsOfflineRecordingEnabled();
  const noteFailedSessionIds = useAtomValue(offlineNoteFailedSessionIdsAtom);
  const dismissedSessionIds = useAtomValue(offlineNoteFailedDismissedSessionIdsAtom);
  const setDismissedSessionIds = useSetAtom(offlineNoteFailedDismissedSessionIdsAtom);

  if (!isOfflineRecordingEnabled || !isTauri()) return null;

  // Dismissal is a permanent per-session mute, by policy: once an id is
  // dismissed, a *repeat* failure on that same id stays suppressed, because the
  // id is already in both lists and `appendUniqueSessionId` no-ops.
  //
  // This is a deliberate choice, not a consequence of replay being impossible —
  // a same-id replay is reachable. If the app exits after
  // `session_engine:note_failed` but before the following `upload_complete`, the
  // tracking row is still `uploading_audio`; on boot `reset_orphan_in_flight_rows`
  // moves it to `pending_sync` and the next drain resubmits the same local id,
  // so `note_failed` can fire again. We keep it muted: the user acknowledged
  // this session's failure, and the remedy (reopen and regenerate) is unchanged
  // by a retry having failed twice.
  const dismissed = new Set(dismissedSessionIds);
  const hasUndismissedFailure = noteFailedSessionIds.some(
    (sessionId) => !dismissed.has(sessionId)
  );

  if (!hasUndismissedFailure) {
    return null;
  }

  const dismissCurrentFailures = () => {
    setDismissedSessionIds((previous) => {
      const next = new Set(previous);
      noteFailedSessionIds.forEach((sessionId) => next.add(sessionId));
      return next.size === previous.length ? previous : [...next];
    });
  };

  return (
    <Banner
      subVariant="subtle"
      role="status"
      aria-live="polite"
      data-testid="offline-note-failed-warning"
      className="shrink-0"
    >
      <BannerContent>
        <BannerTitle>
          <FormattedMessage defaultMessage="Session synced" id="OAJxNsyA25" />
        </BannerTitle>
        <BannerDescription>
          <FormattedMessage
            defaultMessage="Heidi synced your session, but couldn’t generate the note. You can reopen the session and generate it again."
            id="8q8V3EtE8q"
          />
        </BannerDescription>
      </BannerContent>
      <BannerClose
        aria-label={intl.formatMessage(messages.dismiss)}
        onClick={dismissCurrentFailures}
      />
    </Banner>
  );
};
